import React, { memo, useCallback, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Select, ISelectOption } from 'components/forms/Selects'
import { fetchCarriers } from 'apps/fleet-planner/utils/actions/carriers'
import { selectCarrierOptionList } from 'apps/fleet-planner/utils/selectors'

interface ICarrierSelectProps {
  motorCarrierId?: number
  onChange: (option: ISelectOption<number>) => void
  fetchList?: boolean
  large?: boolean
  required?: boolean
  validationMessage?: string
  invalid?: boolean
  defaultCarrier?: boolean
  label?: string
  selectDefaultOnMount?: boolean
  /**
   * @default false
   */
  disabled?: boolean
}

export const CarrierSelect = memo<ICarrierSelectProps>(
  ({
    defaultCarrier = true,
    disabled = false,
    fetchList = true,
    invalid,
    label,
    large,
    motorCarrierId,
    onChange,
    required,
    selectDefaultOnMount = true,
    validationMessage,
  }) => {
    const dispatch = useDispatch()

    const carrierList = useSelector(selectCarrierOptionList)

    const carrierHandler = useCallback((option: ISelectOption<number>) => onChange(option), [
      onChange,
    ])

    /**
     * If only one carrier, and default is true, select that carrier
     */
    useEffect(() => {
      // TODO: This is a total antipattern! We should never trigger onChange events on mount
      if (selectDefaultOnMount && carrierList.length === 1 && defaultCarrier)
        carrierHandler(carrierList[0])
    }, [carrierList, carrierHandler, defaultCarrier, selectDefaultOnMount])

    /**
     * Dispatch fetch on mount, only once
     */
    useEffect(() => {
      if (fetchList) {
        dispatch(fetchCarriers())
      }
    }, [fetchList, dispatch])

    return (
      <Select
        disabled={disabled}
        label={label}
        placeholder='Select a Carrier'
        options={carrierList}
        value={motorCarrierId}
        onChange={carrierHandler}
        large={large}
        required={required}
        validationMessage={validationMessage}
        invalid={invalid}
        dataTest='carrier-select'
      />
    )
  }
)
